/**
 * FontSelector.tsx
 * Composant React pour sélectionner une police avec chargement dynamique des Google Fonts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAppCoordinatorOptional } from '../../../hooks/useAppCoordinator';
import { logger } from '../../../utils/ProfessionalLogger';

export interface FontSelectorProps {
  value: string;
  onChange: (fontFamily: string) => void;
}

export function FontSelector({ value, onChange }: FontSelectorProps): React.ReactElement {
  const appCoordinator = useAppCoordinatorOptional();
  const [fonts, setFonts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Charger les polices au montage
  useEffect(() => {
    const loadFonts = async () => {
      setIsLoading(true);
      try {
        if (appCoordinator) {
          const fontManager = appCoordinator.getFontManager();
          // Charger les Google Fonts depuis l'API
          const allFonts = await fontManager.getAllFonts();
          const fontNames = allFonts.map(f => f.family);

          // Ajouter les polices système
          const systemFonts = [
            'Arial',
            'Helvetica',
            'Times New Roman',
            'Courier New',
            'Georgia',
            'Verdana',
          ];

          // Combiner et trier
          const combinedFonts = [...new Set([...systemFonts, ...fontNames])].sort();
          setFonts(combinedFonts);
        } else {
          // Fallback sur polices système
          setFonts(['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana']);
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des polices', { error }, 'FontSelector');
        setFonts(['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana']);
      } finally {
        setIsLoading(false);
      }
    };

    loadFonts();
  }, []);

  // Filtrer les polices selon la recherche
  const filteredFonts = fonts.filter(font =>
    font.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = useCallback(
    (fontFamily: string) => {
      onChange(fontFamily);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onChange]
  );

  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 12px',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: '14px',
          background: 'var(--color-bg-primary)',
          color: 'var(--color-text-primary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: value,
        }}
      >
        <span>{value}</span>
        <span style={{ fontSize: '12px' }}>▼</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <input
            type="text"
            placeholder="Rechercher une police..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px',
              border: 'none',
              borderBottom: '1px solid var(--color-border)',
              fontSize: '14px',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
            }}
            autoFocus
          />
          <div
            style={{
              overflowY: 'auto',
              maxHeight: '250px',
            }}
          >
            {isLoading ? (
              <div
                style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: 'var(--color-text-secondary)',
                  fontSize: '14px',
                }}
              >
                Chargement des polices...
              </div>
            ) : filteredFonts.length === 0 ? (
              <div
                style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: 'var(--color-text-secondary)',
                  fontSize: '14px',
                }}
              >
                Aucune police trouvée
              </div>
            ) : (
              filteredFonts.map(font => (
                <button
                  key={font}
                  type="button"
                  onClick={() => handleSelect(font)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    background: value === font ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                    color: value === font ? 'var(--color-primary)' : 'var(--color-text-primary)',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: font,
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    if (value !== font) {
                      e.currentTarget.style.background = 'var(--color-bg-secondary)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (value !== font) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {font}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Overlay pour fermer le dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
